import React, { useState, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Link as MuiLink,
} from "@mui/material";
import CollectionsHeader from "../features/curate/Header.js";
import useApi from "../hooks/useApi.js";
import CollectionCover from "../features/curate/Cover.js";
import MotionInView from "../components/MotionInView.js";
import { useInitialBatchSize } from "../hooks/useInitialBatchSize.js";
import { useFillViewport } from "../hooks/useFillViewport.js";
import InfiniteScroll from "react-infinite-scroll-component";
import Loading from "../components/Loading.js";
import Offline from "../components/Offline.js";
import {
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import EmptyCollection from "../features/curate/EmptyCollection.js";
import { useAuth } from "../context/authContext.js";
import { useDetailNavigation } from "../hooks/useDetailNavigation.js";
import { useDocumentMeta } from "../hooks/useDocumentMeta.js";
import useUrlIntent from "../hooks/useUrlIntent.js";
import LibraryBanner from "../features/track/LibraryBanner.js";

export default function Curate() {
  // ─── Auth & context ──────────────────────────────────────────────────────────
  const { user } = useAuth();
  const { toggleDrawer, drawerWidth } = useOutletContext();

  // ─── Routing ─────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const { openList } = useDetailNavigation();
  const { state: locationState } = useLocation();
  const { username: targetUsername } = useParams();
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Derive view from the URL: the /saved sub-path signals the saved view, otherwise mine.
  const isSavedRoute = Boolean(useMatch("/curate/u/:username/saved"));
  const view = isSavedRoute ? "saved" : "mine";

  // ─── Derived constants ────────────────────────────────────────────────────────
  const isOwn = user?.username === targetUsername;

  // Gated on isOwn rather than the /saved URL: get_user_lists (services/list.py)
  // ignores `view` entirely once a target_username is set and silently serves that
  // user's public collections instead — so a non-owner's /saved visit never
  // actually shows saved data, and the title shouldn't claim it does either.
  useDocumentMeta({
    title: targetUsername
      ? `@${targetUsername}'s ${view === "saved" && isOwn ? "saved" : "curated"} collections on Curate`
      : undefined,
  });

  // ─── API ─────────────────────────────────────────────────────────────────────
  const { fetchUserLists } = useApi();

  // ─── Filter / sort state ──────────────────────────────────────────────────────
  const [contentType, setContentType] = useState(
    locationState?.contentType ?? "all",
  );
  const [sortBy, setSortBy] = useState("updated");
  const [sortDescending, setSortDescending] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // ─── Collection state ─────────────────────────────────────────────────────────
  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [newCollectionId, setNewCollectionId] = useState(null); // ID of a newly created collection, used to auto-open it
  // ?add=collection (e.g. Getting started's collection step) opens the create dialog
  useUrlIntent("collection", () => setCreateListDialogOpen(true), {
    enabled: isOwn,
  });

  // ─── Query ────────────────────────────────────────────────────────────────────
  // When viewing another user's collections, pass their username so the backend
  // filters to only their visibility-permitted collections.
  const viewingOtherUser = Boolean(targetUsername && !isOwn);

  const collectionsInfiniteQuery = useInfiniteQuery({
    queryKey: [
      "infCollections",
      contentType,
      { sortBy, sortDescending, searchTerm, view, targetUsername },
    ],
    queryFn: ({ pageParam = 1 }) =>
      fetchUserLists({
        pageParam,
        perPage: 5,
        sortDescending,
        sortBy,
        searchTerm,
        contentType,
        view,
        username: viewingOtherUser ? targetUsername : null,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

  const collectionsData = React.useMemo(
    () =>
      collectionsInfiniteQuery.data?.pages.flatMap((page) => page.items) || [],
    [collectionsInfiniteQuery.data],
  );

  useFillViewport(collectionsInfiniteQuery);

  // Only the batch loaded on first render of the current filter/sort context
  // gets an entrance animation — cards appended afterward via infinite scroll
  // (or the current batch, once already settled) should never animate again.
  const collectionsGenerationKey = JSON.stringify([
    contentType,
    sortBy,
    sortDescending,
    searchTerm,
    view,
    targetUsername,
  ]);
  const initialBatchSize = useInitialBatchSize(
    collectionsData,
    collectionsGenerationKey,
  );

  // ─── Auto-open newly created collection ───────────────────────────────────────
  // Waits for the new collection to appear in collectionsData (i.e. the list
  // query has refetched) before opening it, since openList needs the full
  // object (for its title/slug), not just the id handed back on creation.
  useEffect(() => {
    if (!newCollectionId) return;
    const created = collectionsData.find(
      (list) => list.list_id === newCollectionId,
    );
    if (created) {
      setNewCollectionId(null);
      openList(created);
    }
  }, [collectionsData, newCollectionId, openList]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  const handleContentChange = (newValue) => {
    setContentType(newValue);
  };

  // Navigate between mine / saved views via URL
  const handleViewChange = (newValue) => {
    if (newValue === "mine") {
      navigate(`/curate/u/${user.username}`);
    } else {
      navigate(`/curate/u/${user.username}/saved`);
    }
  };

  const handleSortByChange = (event, newSortBy) => {
    if (newSortBy !== null) setSortBy(newSortBy);
  };

  const toggleSortDescending = () => {
    setSortDescending((prev) => !prev);
  };

  const handleSearchTermChange = (newValue) => {
    setSearchTerm(newValue);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <CollectionsHeader
        contentType={contentType}
        onContentChange={handleContentChange}
        view={view}
        setView={handleViewChange}
        sortBy={sortBy}
        onSortByChange={handleSortByChange}
        sortDescending={sortDescending}
        toggleSortDescending={toggleSortDescending}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        toggleDrawer={toggleDrawer}
        setNewCollectionId={setNewCollectionId}
        drawerWidth={drawerWidth}
        createListDialogOpen={createListDialogOpen}
        setCreateListDialogOpen={setCreateListDialogOpen}
        viewingOtherUser={viewingOtherUser}
      />

      {viewingOtherUser && (
        <LibraryBanner
          content={
            <Typography
              sx={{
                flex: 1,
                textAlign: {
                  xs: "left",
                  sm: "center",
                },
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                },
              }}
            >
              You're viewing collections curated by{" "}
              <MuiLink
                component={RouterLink}
                to={`/u/${targetUsername}`}
                sx={{ mr: 0.5 }}
              >
                <i>@{targetUsername}</i>
              </MuiLink>
            </Typography>
          }
          onClose={
            user
              ? () => navigate(`/curate/u/${user.username}`)
              : () => navigate(`/curate`)
          }
        />
      )}

      {/* isError here is only ever a network error — server errors throw and are caught by QueryErrorBoundary */}
      {collectionsInfiniteQuery.isError && <Offline />}

      {/* Loading state */}
      {collectionsInfiniteQuery.isPending && (
        <Box
          width="100%"
          height="75dvh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Loading />
        </Box>
      )}

      {/* Empty state — gated on isSuccess so neither the empty state nor the grid
          renders during a network error (the Offline screen above owns that case). */}
      {collectionsInfiniteQuery.isSuccess && collectionsData.length === 0 ? (
        <EmptyCollection
          filtersApplied={searchTerm !== ""}
          contentType={contentType}
          onAdd={() => setCreateListDialogOpen(true)}
          view={view}
          isOwn={isOwn}
        />
      ) : collectionsInfiniteQuery.isSuccess ? (
        <>
          {/* Collections grid */}
          <InfiniteScroll
            dataLength={collectionsData.length}
            next={collectionsInfiniteQuery.fetchNextPage}
            hasMore={!!collectionsInfiniteQuery.hasNextPage}
            loader={
              <Box
                sx={{
                  pt: 2,
                  pb: 2,
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Loading inline />
              </Box>
            }
            style={{ overflow: "visible" }}
            // ^ Required to ensure framer-motion scale animation on library cards displays correctly
          >
            <Grid container sx={{ pt: 1, pb: 2 }} spacing={2}>
              {collectionsData.map((list, index) => {
                const card = (
                  <CollectionCover
                    list={list}
                    reverse={index % 2 === 0}
                    onClick={() => openList(list)}
                    isMobile={fullscreen}
                    showAuthor={view === "saved"}
                    isOwn={isOwn}
                  />
                );
                return (
                  <Grid item xs={12} key={list.list_id}>
                    {index < initialBatchSize ? (
                      <MotionInView inView delay={Math.min(index, 6) * 0.05}>
                        {card}
                      </MotionInView>
                    ) : (
                      card
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </InfiniteScroll>
        </>
      ) : null}
    </Box>
  );
}
